import React, { useState, useEffect } from 'react';
import { sharedStyles, getStatusColor, formatDate } from '../styles/sharedStyles';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { hideFinancialUi } from '../utils/foremanUi';
import ViewEditTicketModal from '../components/ViewEditTicketModal';
import AddTicketForm from '../components/AddTicketForm';
import DuplicateTicketModal from '../components/DuplicateTicketModal';

const API_BASE_URL = process.env.API_BASE_URL ;

const Jobs = () => {
  const api = useApi();
  const { user } = useUser();
  const canManage = user?.role === 'admin';
  const hideMoney = hideFinancialUi(user?.role);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('');
  
  // New state for tickets functionality
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [selectedJobTickets, setSelectedJobTickets] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editTicket, setEditTicket] = useState(false);

  const [formData, setFormData] = useState({
    title: 'N/A' + Math.random().toString(36).substring(2, 15),
    customerId: '',
    description: 'N/A',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    status: 'Pending',
    priority: 'Medium',
    location: 'N/A',
    budget: '0',
    notes: 'N/A'
  });

  const [showAddTicketModal, setShowAddTicketModal] = useState(false);
  const [addTicketJob, setAddTicketJob] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [ticketToDuplicate, setTicketToDuplicate] = useState(null);
  const [employees, setEmployees] = useState([]);

  // Fetch customers for dropdown
  const fetchCustomers = async () => {
    try {
      const response = await api({
        method: 'get',
        url: '/customers/dropdown'
      });
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  // Fetch employees for dropdown
  const fetchEmployees = async () => {
    try {
      const response = await api({
        method: 'get',
        url: `/employees?limit=1000`
      });
      if (response.data.success) {
        setEmployees(response.data.data.employees || response.data.data);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  // Fetch jobs with pagination and filters
  const fetchJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(selectedStatus && { status: selectedStatus }),
        ...(selectedCustomer && { customerId: selectedCustomer })
      });

      const response = await api({
        method: 'get',
        url: `/jobs?${params}`
      });
      
      if (response.data.success) {
        setJobs(response.data.data.jobs);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.response?.data?.message || 'Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [currentPage, searchTerm, selectedStatus, selectedCustomer]);

  // Add effect to handle body scroll when modal is open
  useEffect(() => {
    if (showModal || showEditModal || showTicketsModal || showTicketModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showEditModal, showTicketsModal, showTicketModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for budget field
    if (name === 'budget') {
      const formattedValue = formatBudgetInput(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Parse budget value before sending to server
      const submitData = {
        ...formData,
        budget: parseBudgetValue(formData.budget)
      };
      
      const response = await api({
        method: 'post',
        url: `/jobs`,
        data: submitData
      });
      
      if (response.data.success) {
        await fetchJobs();
        handleCloseModal();
      }
    } catch (err) {
      console.error('Error creating job:', err);
      setError(err.response?.data?.message || 'Failed to create job');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (job) => {
    setEditingJob(job);
    setFormData({
      title: job.title || 'N/A' + Math.random().toString(36).substring(2, 15),
      customerId: job.customerId || '',
      description: job.description || 'N/A',
      startDate: job.startDate ? job.startDate.split('T')[0] : new Date().toISOString().split('T')[0],
      endDate: job.endDate ? job.endDate.split('T')[0] : new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      status: job.status || 'Pending',
      priority: job.priority || 'Medium',
      location: job.location || 'N/A',
      budget: job.budget ? formatBudgetInput(job.budget.toString()) : '0',
      notes: job.notes || 'N/A'
    });
    setShowEditModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Parse budget value before sending to server
      const submitData = {
        ...formData,
        budget: parseBudgetValue(formData.budget)
      };
      
      const response = await api({
        method: 'put',
        url: `/jobs/${editingJob.id}`,
        data: submitData
      });
      
      if (response.data.success) {
        await fetchJobs();
        handleCloseEditModal();
      }
    } catch (err) {
      console.error('Error updating job:', err);
      setError(err.response?.data?.message || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        setLoading(true);
        const response = await api({
          method: 'delete',
          url: `/jobs/${id}`
        });
        
        if (response.data.success) {
          fetchJobs();
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete job');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      title: 'N/A' + Math.random().toString(36).substring(2, 15),
      customerId: '',
      description: 'N/A',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      status: 'Pending',
      priority: 'Medium',
      location: 'N/A',
      budget: '0',
      notes: 'N/A'
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingJob(null);
    setFormData({
      title: 'N/A' + Math.random().toString(36).substring(2, 15),
      customerId: '',
      description: 'N/A',
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
      status: 'Pending',
      priority: 'Medium',
      location: 'N/A',
      budget: '0',
      notes: 'N/A'
    });
  };

  // New functions for tickets functionality
  const handleViewTickets = async (job) => {
    try {
      setTicketsLoading(true);
      setSelectedJob(job);
      
      const response = await api({
        method: 'get',
        url: `/tickets?jobId=${job.id}&limit=1000`
      });
      
      if (response.data.success) {
        setSelectedJobTickets(response.data.data.tickets);
        setShowTicketsModal(true);
      }
    } catch (err) {
      console.error('Error fetching tickets for job:', err);
      setError('Failed to fetch tickets for this job');
    } finally {
      setTicketsLoading(false);
    }
  };

  const handleViewTicket = (ticket, edit = false) => {
    setSelectedTicket(ticket);
    setEditTicket(edit);
    setShowTicketModal(true);
  };

  const handleCloseTicketsModal = () => {
    setShowTicketsModal(false);
    setSelectedJob(null);
    setSelectedJobTickets([]);
  };

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedTicket(null);
    setEditTicket(false);
  };

  const handleTicketUpdated = () => {
    // Refresh tickets for the current job
    if (selectedJob) {
      handleViewTickets(selectedJob);
    }
  };

  const handleDuplicateTicket = (ticket) => {
    setTicketToDuplicate(ticket);
    setShowDuplicateModal(true);
  };

  const handleDuplicateTicketSubmit = async (ticketId, formData) => {
    try {
      const response = await api({
        method: 'post',
        url: `/tickets/${ticketId}/duplicate`,
        data: formData
      });

      if (response.data.success) {
        // Refresh tickets for the current job
        if (selectedJob) {
          handleViewTickets(selectedJob);
        }
        return Promise.resolve();
      } else {
        throw new Error(response.data.message || 'Error duplicating ticket');
      }
    } catch (err) {
      console.error('Error duplicating ticket:', err);
      throw new Error(err.response?.data?.message || 'Error duplicating ticket');
    }
  };

  const handleCloseDuplicateModal = () => {
    setShowDuplicateModal(false);
    setTicketToDuplicate(null);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Pending':
        return 'bg-warning text-dark';
      case 'Approved':
        return 'bg-success text-white';
      case 'Rejected':
        return 'bg-danger text-white';
      case 'Paid':
        return 'bg-primary text-white';
      default:
        return 'bg-secondary text-white';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatBudgetInput = (value) => {
    // Remove all non-digit characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = numericValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Format with commas for thousands
    const number = parseFloat(numericValue) || 0;
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(number);
  };

  const parseBudgetValue = (value) => {
    // Remove commas and convert to number
    return parseFloat(value.replace(/,/g, '')) || 0;
  };

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <h2 style={sharedStyles.pageTitle}>Job Management</h2>
        {canManage && (
          <button 
            className="btn"
            style={sharedStyles.button.primary}
            onClick={() => setShowModal(true)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Job
          </button>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="card mb-4" style={sharedStyles.card}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search by title or description"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Not Completed">Not Completed</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="On Hold">On Hold</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
                <option value="15% Completed">15% Complete</option>
                <option value="30% Completed">30% Complete</option>
                <option value="60% Completed">60% Complete</option>
                <option value="90% Completed">90% Complete</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-select"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
              >
                <option value="">All Customers</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.companyName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Jobs Table */}
      <div className="card" style={sharedStyles.card}>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle mb-0" style={sharedStyles.table}>
                            <thead>
                <tr>
                  <th>Job ID</th>
                  <th>Job Name</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Priority</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Location</th>
                  {!hideMoney && <th>Budget</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => {
                  const statusStyle = getStatusColor(job.status);
                  const priorityStyle = {
                    Low: { bg: '#e3f2fd', text: '#1976d2' },
                    Medium: { bg: '#fff3e0', text: '#f57c00' },
                    High: { bg: '#fce4ec', text: '#c2185b' },
                    Urgent: { bg: '#ffebee', text: '#d32f2f' }
                  }[job.priority];

                  return (
                    <tr key={job.id}>
                      <td>
                        <div className="fw-medium">{job.id}</div>
                      </td>
                      <td>{job.title}</td>
                      <td>{job.customer?.companyName || 'N/A'}</td>
                      <td>
                        <span 
                          style={{
                            ...sharedStyles.statusBadge,
                            backgroundColor: statusStyle.bg,
                            color: statusStyle.text
                          }}
                        >
                          {job.status}
                        </span>
                      </td>
                      <td>
                        <span 
                          style={{
                            ...sharedStyles.statusBadge,
                            backgroundColor: priorityStyle.bg,
                            color: priorityStyle.text
                          }}
                        >
                          {job.priority}
                        </span>
                      </td>
                      <td>{job.startDate ? formatDate(job.startDate) : '-'}</td>
                      <td>{job.endDate ? formatDate(job.endDate) : '-'}</td>
                      <td>{job.location || '-'}</td>
                      {!hideMoney && <td>{job.budget ? formatCurrency(job.budget) : '-'}</td>}
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => handleViewTickets(job)}
                            title="View Tickets"
                          >
                            <i className="bi bi-ticket-detailed"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => { setAddTicketJob(job); setShowAddTicketModal(true); }}
                            title="Add Ticket"
                          >
                            <i className="bi bi-plus-circle"></i>
                          </button>
                          {canManage && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleEdit(job)}
                                title="Edit Job"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(job.id)}
                                title="Delete Job"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <nav className="mt-4" style={sharedStyles.pagination} aria-label="Job table pagination">
            <ul className="pagination justify-content-center mb-0 flex-wrap">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Add Job Modal */}
      {showModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Add New Job</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    {/* Required Fields */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Customer *</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="customerId"
                          value={formData.customerId}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">Select Customer</option>
                          {customers.map(customer => (
                            <option key={customer.id} value={customer.id}>
                              {customer.companyName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Job Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Description</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Status and Priority */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Not Completed">Not Completed</option>
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="On Hold">On Hold</option>
                          <option value="Completed">Completed</option>
                          <option value="15% Completed">15% Complete</option>
                          <option value="30% Completed">30% Complete</option>
                          <option value="60% Completed">60% Complete</option>
                          <option value="90% Completed">90% Complete</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Priority</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="priority"
                          value={formData.priority}
                          onChange={handleInputChange}
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    {/* Location and Budget */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Location</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Budget</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          placeholder="Enter budget amount"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Notes</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Adding...
                          </>
                        ) : (
                          'Add Job'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Job Modal */}
      {showEditModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseEditModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Edit Job</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseEditModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleUpdateSubmit}>
                    {/* Required Fields */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Customer</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="customerId"
                          value={formData.customerId}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Customer</option>
                          {customers.map(customer => (
                            <option key={customer.id} value={customer.id}>
                              {customer.companyName}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Job Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Description */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Description</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Status and Priority */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Not Completed">Not Completed</option>
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="On Hold">On Hold</option>
                          <option value="Completed">Completed</option>
                          <option value="15% Completed">15% Complete</option>
                          <option value="30% Completed">30% Complete</option>
                          <option value="60% Completed">60% Complete</option>
                          <option value="90% Completed">90% Complete</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Priority</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="priority"
                          value={formData.priority}
                          onChange={handleInputChange}
                        >
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    {/* Location and Budget */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Location</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Budget</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          placeholder="Enter budget amount"
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Notes</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseEditModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Updating...
                          </>
                        ) : (
                          'Update Job'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tickets Modal */}
      {showTicketsModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseTicketsModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>
                    Tickets for Job: {selectedJob?.title}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseTicketsModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger alert-dismissible fade show" role="alert">
                      {error}
                      <button type="button" className="btn-close" onClick={() => setError('')}></button>
                    </div>
                  )}
                  {ticketsLoading ? (
                    <div className="text-center my-4">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : selectedJobTickets.length === 0 ? (
                    <div className="text-center my-4">
                      <p className="text-muted">No tickets found for this job.</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0" style={sharedStyles.table}>
                        <thead>
                          <tr>
                            <th>Ticket #</th>
                            <th>Work Date</th>
                            <th>Status</th>
                            {!hideMoney && <th>Total Amount</th>}
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedJobTickets.map(ticket => (
                            <tr key={ticket.id}>
                              <td>{ticket.ticketNumber}</td>
                              <td>{ticket.workDate ? formatDate(ticket.workDate) : '-'}</td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(ticket.status)}`}>
                                  {ticket.status}
                                </span>
                              </td>
                              {!hideMoney && (
                                <td>
                                  {formatCurrency(
                                    ticket.TicketItems?.reduce(
                                      (sum, item) => sum + (isNaN(Number(item.amount)) ? 0 : Number(item.amount)),
                                      0
                                    ) || 0
                                  )}
                                </td>
                              )}
                              <td>
                                <div className="btn-group">
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleViewTicket(ticket, false)}
                                    title="View Ticket"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleViewTicket(ticket, true)}
                                    title="Edit Ticket"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button
                                    className="btn btn-sm btn-outline-info"
                                    onClick={() => handleDuplicateTicket(ticket)}
                                    title="Duplicate Ticket"
                                  >
                                    <i className="bi bi-files"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button 
                    type="button" 
                    className="btn"
                    style={sharedStyles.button.secondary}
                    onClick={handleCloseTicketsModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Individual Ticket Modal */}
      {showTicketModal && selectedTicket && (
        <ViewEditTicketModal
          ticket={selectedTicket}
          onClose={handleCloseTicketModal}
          onUpdated={handleTicketUpdated}
          edit={editTicket}
        />
      )}

      {showAddTicketModal && addTicketJob && (
        <AddTicketForm
          onSuccess={() => { setShowAddTicketModal(false); setAddTicketJob(null); fetchJobs(); }}
          onClose={() => { setShowAddTicketModal(false); setAddTicketJob(null); }}
          prefillJob={addTicketJob}
        />
      )}

      {/* Duplicate Ticket Modal */}
      <DuplicateTicketModal
        show={showDuplicateModal}
        onClose={handleCloseDuplicateModal}
        originalTicket={ticketToDuplicate}
        onDuplicate={handleDuplicateTicketSubmit}
        customers={customers}
        jobs={jobs}
        employees={employees}
      />
    </div>
  );
};

export default Jobs; 